// Packages and components
import React, { useEffect, useState } from 'react';
import { Apple, Facebook, Globe, Google } from 'grommet-icons';
import Cookies from 'universal-cookie';

// Helpers and utilities
import { analytics } from '@services/analytics';
import { colors } from '@theme/globals/colors';
import { Sizes } from '@theme/globals/sizing';
import { toSentenceCase } from '@theme-utils/type';

// Styles
import { StyledAuthButton } from './SHDAuthButton.styles';

// Types
import type { SHDAuthButtonProps } from './SHDAuthButton.d';
import type { dtos } from '@shd/jslib/models';

const cookies = new Cookies();

const SHDAuthButton: React.FC<SHDAuthButtonProps> = ({
  className = 'button-auth',
  firebaseProvider,
  inviteCode,
  onError,
  onAuthenticate,
  provider,
  trackingContext,
  trackingEvent,
  trackingProperties,

  ...props
}) => {
  // Constants
  const buttonText = `Continue with ${toSentenceCase(provider)}`;
  const providerIconDefaultProps = {
    color: 'plain',
    size: Sizes[16],
    role: 'img',
  };

  const postParams: Partial<dtos.UserPostRequest> = {
    create_if_not_exist: true,
  };

  // States
  const [extraPostParams, setExtraPostParams] =
    useState<Partial<dtos.UserPostRequest>>(postParams);

  useEffect(() => {
    if (inviteCode) {
      setExtraPostParams({
        ...extraPostParams,
        invite_code: inviteCode,
      });
    }

    const referrerUserId = localStorage.getItem('referrerUserId');
    if (referrerUserId) {
      setExtraPostParams({
        ...extraPostParams,
        referrer_user_id: referrerUserId,
      });
    }

    const referralCode = cookies.get('referralCode');
    if (referralCode) {
      setExtraPostParams({
        ...extraPostParams,
        referral_code: referralCode,
      });
    }

    const externalEmail = cookies.get('externalEmail');
    if (externalEmail) {
      setExtraPostParams({
        ...extraPostParams,
        external_email: externalEmail,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Methods
  const getProviderIcon = (providerName: string) => {
    switch (providerName) {
      case 'google':
        return <Google {...providerIconDefaultProps} />;
      case 'facebook':
        return <Facebook {...providerIconDefaultProps} />;
      case 'apple':
        return <Apple {...providerIconDefaultProps} color={colors.apple} />;
      default:
        return <Globe {...providerIconDefaultProps} />;
    }
  };

  // Handlers
  const handleButtonTracking = () => {
    if (!trackingEvent) {
      return;
    }

    if (trackingEvent && !trackingContext) {
      onError('Tracking context is required when tracking event is provided');
    }

    analytics.track(trackingEvent, {
      context: trackingContext,
      ...trackingProperties,
    });
  };

  return (
    <StyledAuthButton
      {...props}
      className={className}
      fill="horizontal"
      icon={getProviderIcon(provider)}
      label={buttonText}
      onClick={() => {
        handleButtonTracking();
        onAuthenticate(undefined, undefined, extraPostParams, firebaseProvider);
      }}
    />
  );
};

export default SHDAuthButton;
